import { Link } from "react-router-dom";
import { Play, Clock, Radio, Eye } from "lucide-react";

function formatTime(iso) {
  if (!iso) return "TBD";
  const d = new Date(iso);
  if (isNaN(d)) return iso;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

const FALLBACK = "/default-thumb.png";

export const GameCard = ({ game, viewers = 0 }) => {
  const isLive = game.status === "live";
  return (
    <Link
      to={`/watch/${game.id}`}
      data-testid={`game-card-${game.id}`}
      className="group block border border-white/10 bg-[#141414] transition-transform duration-300 ease-out hover:scale-[1.02] hover:border-white/25 focus:outline-none focus:ring-2 focus:ring-[#007AFF]"
    >
      <div className="relative aspect-video overflow-hidden">
        <img
          src={game.thumbnail_url || FALLBACK}
          alt={`${game.team_a} vs ${game.team_b}`}
          className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-105"
          onError={(e) => {
            e.currentTarget.src = FALLBACK;
          }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A] via-transparent to-transparent" />
        {isLive && (
          <div className="absolute left-3 top-3 flex items-center gap-1.5 bg-[#007AFF] px-2.5 py-1">
            <span className="relative flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-white opacity-75" />
              <span className="relative inline-flex h-2 w-2 rounded-full bg-white" />
            </span>
            <span className="font-display text-xs font-bold uppercase tracking-widest text-white">
              Live
            </span>
          </div>
        )}
        {game.status === "finished" && (
          <div className="absolute left-3 top-3 bg-black/70 px-2.5 py-1 font-display text-xs font-bold uppercase tracking-widest text-white/80">
            Replay
          </div>
        )}
        <div className="absolute right-3 top-3 flex h-9 w-9 items-center justify-center bg-[#0A0A0A]/70 opacity-0 backdrop-blur transition-opacity group-hover:opacity-100">
          {isLive ? <Radio className="h-4 w-4 text-[#007AFF]" /> : <Play className="h-4 w-4 text-white" />}
        </div>
        {isLive && viewers > 0 && (
          <div data-testid={`viewer-badge-${game.id}`} className="absolute bottom-3 right-3 flex items-center gap-1.5 bg-[#0A0A0A]/80 px-2.5 py-1 font-display text-xs font-bold uppercase tracking-widest text-white backdrop-blur">
            <Eye className="h-3.5 w-3.5 text-[#007AFF]" />
            {viewers.toLocaleString()} Watching
          </div>
        )}
      </div>
      <div className="p-4">
        {game.league && (
          <p className="mb-2 font-display text-[0.65rem] font-bold uppercase tracking-[0.2em] text-[#007AFF]">
            {game.league}
          </p>
        )}
        <div className="flex items-center gap-2">
          <TeamBadge name={game.team_a} logo={game.team_a_logo} color={game.team_a_color} />
          <span className="font-display text-sm font-bold uppercase text-white/30">vs</span>
          <TeamBadge name={game.team_b} logo={game.team_b_logo} color={game.team_b_color} align="right" />
        </div>
        <div className="mt-3 flex items-center gap-1.5 text-white/50">
          <Clock className="h-3.5 w-3.5" />
          <span className="text-sm">{formatTime(game.start_time)}</span>
        </div>
      </div>
    </Link>
  );
};

const TeamBadge = ({ name, logo, color, align }) => (
  <div className={`flex min-w-0 flex-1 items-center gap-2 ${align === "right" ? "flex-row-reverse text-right" : ""}`}>
    {logo ? (
      <img
        src={logo}
        alt={name}
        className="h-8 w-8 shrink-0 object-contain"
        onError={(e) => {
          e.currentTarget.style.display = "none";
        }}
      />
    ) : (
      <span
        className="flex h-8 w-8 shrink-0 items-center justify-center font-display text-sm font-black text-white"
        style={{ backgroundColor: color || "#1F1F1F" }}
      >
        {(name || "?").slice(0, 1).toUpperCase()}
      </span>
    )}
    <span className="truncate font-display text-lg font-bold uppercase leading-tight tracking-tight text-white">
      {name}
    </span>
  </div>
);
